import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import EditStaffPage from './EditStaffPage';
import { FaArrowLeft, FaArrowRight, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';

function Staff() {
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingStaff, setEditingStaff] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'ascending' });
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [showEditPage, setShowEditPage] = useState(false);

  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const staffCollection = collection(db, 'staff');
        const staffSnapshot = await getDocs(staffCollection);
        const staffData = staffSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setStaffList(staffData);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching staff: ", error);
        setLoading(false);
      }
    };

    fetchStaff();
  }, []);

  const handleEdit = (staff) => {
    setEditingStaff(staff);
    setShowEditPage(true);
  };

  const handleSaveEdit = async (editedStaff) => {
    try {
      const staffRef = doc(db, 'staff', editedStaff.id);
      await updateDoc(staffRef, {
        name: editedStaff.name,
        hireDate: editedStaff.hireDate
      });
      setStaffList(staffList.map(staff => 
        staff.id === editedStaff.id ? editedStaff : staff
      ));
      setEditingStaff(null);
      setShowEditPage(false);
    } catch (error) {
      console.error("Error updating staff: ", error);
    }
  };

  const handleDelete = async (staffId) => {
    if (window.confirm('Are you sure you want to delete this staff member?')) {
      try {
        await deleteDoc(doc(db, 'staff', staffId));
        setStaffList(staffList.filter(staff => staff.id !== staffId));
      } catch (error) {
        console.error("Error deleting staff: ", error);
      }
    }
  };

  const handleCloseEditPage = () => {
    setEditingStaff(null);
    setShowEditPage(false);
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handleEntriesPerPageChange = (event) => {
    setEntriesPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => (prev * entriesPerPage < filteredAndSortedStaff.length ? prev + 1 : prev));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'ascending' ? <FaSortUp /> : <FaSortDown />;
    }
    return <FaSort />;
  };

  const filteredAndSortedStaff = staffList
    .filter(staff => 
      staff.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      staff.hireDate.toDate().toLocaleDateString().includes(searchTerm)
    )
    .sort((a, b) => {
      if (sortConfig.key === 'name') {
        return sortConfig.direction === 'ascending' 
          ? a.name.localeCompare(b.name) 
          : b.name.localeCompare(a.name);
      } else {
        return sortConfig.direction === 'ascending' 
          ? a.hireDate.toDate() - b.hireDate.toDate() 
          : b.hireDate.toDate() - a.hireDate.toDate();
      }
    });

  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = filteredAndSortedStaff.slice(indexOfFirstEntry, indexOfLastEntry);

  return (
    <div className="Staff p-4">
      <h1 className="text-3xl font-serif mb-4">Staff List</h1>
      {loading ? (
        <p className="text-center">Loading...</p>
      ) : showEditPage ? (
        <EditStaffPage
          staff={editingStaff}
          onSave={handleSaveEdit}
          onClose={handleCloseEditPage}
        />
      ) : (
        <>
          <div className="mb-4 flex justify-between items-center">
            <div className="flex items-center">
              <label className="mr-2">Entries per page:</label>
              <select
                value={entriesPerPage}
                onChange={handleEntriesPerPageChange}
                className="px-2 py-1 border border-gray-300 rounded"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <div className="flex items-center ml-4">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={handleSearch}
                className="px-4 py-2 border border-gray-300 rounded"
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border-gray-200 shadow-md rounded-lg">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="py-2 px-4 border-b border-gray-200 text-center">#</th>
                  {['name', 'hireDate'].map((column) => (
                    <th 
                      key={column}
                      className="py-2 px-4 border-b border-gray-200 text-center cursor-pointer"
                      onClick={() => handleSort(column)}
                    >
                      <div className="flex items-center justify-center">
                        {column === 'name' ? 'Staff Name' : 'Hire Date'}
                        {getSortIcon(column)}
                      </div>
                    </th>
                  ))}
                  <th className="py-2 px-4 border-b border-gray-200 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {currentEntries.map((staff, index) => (
                  <tr key={staff.id} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="py-2 px-4 border-b border-gray-200 text-center">{index + 1 + indexOfFirstEntry}</td>
                    <td className="py-2 px-4 border-b border-gray-200 text-center">{staff.name}</td>
                    <td className="py-2 px-4 border-b border-gray-200 text-center">{staff.hireDate.toDate().toLocaleDateString()}</td>
                    <td className="py-2 px-4 border-b border-gray-200 text-center">
                      <button 
                        className="mr-2 px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                        onClick={() => handleEdit(staff)}
                      >
                        Edit
                      </button>
                      <button 
                        className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                        onClick={() => handleDelete(staff.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-between mt-4">
            <div className="flex space-x-2">
              <button
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
              >
                <FaArrowLeft />
              </button>
              <button
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                onClick={handleNextPage}
                disabled={indexOfLastEntry >= filteredAndSortedStaff.length}
              >
                <FaArrowRight />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Staff;